import React, { useState } from 'react';

import { buttonStyle, selectColor, deviceColor } from '../../../styles/constants';
import type { Device, TimeFilterBoxProps } from '../../../types';
import { formatAge } from '../../../utils/dateUtils';
import { useRedrawCounter } from '../../../utils/hooks';

/**
 * TimeFilterBox Component - Expandable list of filtered devices
 *
 * Shows a count of devices matching a time-based filter criterion.
 * Can be expanded to show the full list of matching devices.
 * Used for showing alarms, unpositioned devices, or offline devices.
 *
 * @example
 * <TimeFilterBox
 *   position={0}
 *   devices={devices}
 *   predicate={device => hasActiveAlarm(device)}
 *   getAge={device => getAlarmAgeMinutes(device)}
 *   text="Alarms"
 *   getValue={() => selectedDevice}
 *   setValue={setSelectedDevice}
 * />
 */
export const TimeFilterBox: React.FC<TimeFilterBoxProps> = ({
  devices = [],
  position = 0,
  predicate = () => false,
  getAge = () => 0,
  text = 'Items',
  getValue = () => null,
  setValue = () => {},
}) => {
  const [expand, setExpand] = useState(false);

  // Auto-refresh component every 5 seconds to update ages in display
  // Only when component is expanded (visible)
  useRedrawCounter(expand ? 5000 : null);

  // Filter devices based on predicate
  const filteredDevices = devices.filter(predicate);

  // Don't render if no devices match
  if (!filteredDevices.length) {
    return null;
  }

  const selectedDeviceId = getValue();

  const containerStyle: React.CSSProperties = {
    ...buttonStyle,
    textAlign: 'left',
    background: '#eeec',
    position: 'fixed',
    right: `${position * 33}vw`,
    bottom: '12vmin',
    width: '29vw',
    fontSize: '2.5vmin',
    maxHeight: '30vh',
    borderWidth: '.1vmin',
    borderColor: 'blue',
    borderStyle: 'solid',
    zIndex: 2000,
  };

  const scrollAreaStyle: React.CSSProperties = {
    overflowY: 'auto',
    maxHeight: '25vh',
  };

  const deviceItemStyle = (device: Device): React.CSSProperties => ({
    fontSize: '1.8vmin',
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    background: selectedDeviceId === device.id ? selectColor : deviceColor,
    margin: '0.5vmin',
    padding: '0.5vmin',
    cursor: 'pointer',
    borderRadius: '0.3vmin',
  });

  const statusIndicatorStyle = (hasAlarm: boolean): React.CSSProperties => ({
    height: '1.3vmin',
    width: '1.3vmin',
    margin: '0.5vmin',
    borderRadius: '1vmin',
    background: hasAlarm ? '#f00' : '#ccc',
    flexShrink: 0,
  });

  const toggleButtonStyle: React.CSSProperties = {
    textAlign: 'center',
    color: 'red',
    cursor: 'pointer',
    padding: '0.5vmin',
    fontWeight: 'bold',
  };

  /**
   * Get device display name (prioritize title over text)
   */
  const getDeviceDisplayName = (device: Device): string => {
    return device.title && device.title.length > 0 ? device.title : device.text || '';
  };

  /**
   * Check if device has an active alarm
   */
  const hasActiveAlarm = (device: Device): boolean => {
    if (!device.alarmTime) return false;

    const now = new Date().getTime();
    const alarmTime = new Date(device.alarmTime).getTime();
    const ageMinutes = (now - alarmTime) / (1000 * 60);

    return ageMinutes >= 0;
  };

  return (
    <div style={containerStyle}>
      {/* Expandable device list */}
      {expand && (
        <div style={scrollAreaStyle}>
          {filteredDevices.map((device) => {
            const ageInSeconds = getAge(device) * 60; // Convert minutes to seconds

            return (
              <div
                key={device.id}
                style={deviceItemStyle(device)}
                onClick={() => setValue(device.id)}
                title={`Select ${getDeviceDisplayName(device)}`}
              >
                {/* Status indicator (red for alarm, gray for normal) */}
                <div style={statusIndicatorStyle(hasActiveAlarm(device))} />

                {/* Device name and age */}
                <span>
                  {getDeviceDisplayName(device)} | {formatAge(ageInSeconds)}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {/* Separator line when expanded */}
      {expand && <hr style={{ margin: '0.5vmin 0', borderColor: '#ccc' }} />}

      {/* Toggle button with count */}
      <div
        style={toggleButtonStyle}
        onClick={() => setExpand(!expand)}
        title={expand ? 'Collapse list' : 'Expand list'}
      >
        {filteredDevices.length} {text}
      </div>
    </div>
  );
};

export default TimeFilterBox;
